import { useState, useRef, useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  Search,
  MessageSquare,
  ThumbsUp,
  ThumbsDown,
  Star,
  Archive,
  Reply,
  Sparkles,
  ArrowLeft,
  Send,
  X,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  RefreshCw,
  PanelRightClose,
  Minus,
  Coffee,
  RotateCcw,
} from "lucide-react";
import { type LucideIcon } from "lucide-react";
import { useEmailProvidersQuery } from "../../hooks/useEmailProviders";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  useInboxQuery,
  useInboxCountsQuery,
  useInboxMessageQuery,
  useInboxFiltersQuery,
  useReplyMutation,
  useUpdateStatusMutation,
  useToggleStarMutation,
  useMarkReadMutation,
  useSyncInboxMutation,
  useInboxRealtime,
  type InboxProvider,
  type InboxThreadDetail,
  type InboxListParams,
  type InboxMeta,
} from "../../hooks/useInbox";
import { get } from "../../lib/apiClient";
import { API_URL } from "../../_config";
import { useAuth } from "../../context/AuthContext";
import ReplyRichEditor, { type ReplyRichEditorHandle } from "../../components/replies/ReplyRichEditor";

interface SentimentConfigItem {
  label: string;
  icon: LucideIcon;
  color: string;
  bg: string;
}

interface StatusTab {
  key: string;
  label: string;
}

const sentimentConfig: Record<string, SentimentConfigItem> = {
  positive: { label: "Positive", icon: ThumbsUp, color: "#4CAF50", bg: "#E8F5E9" },
  interested: { label: "Interested", icon: Star, color: "#F5A623", bg: "#FFF8E1" },
  negative: { label: "Negative", icon: ThumbsDown, color: "#E53935", bg: "#FFEBEE" },
  neutral: { label: "Neutral", icon: Minus, color: "#607D8B", bg: "#ECEFF1" },
  out_of_office: { label: "Out of Office", icon: Coffee, color: "#0288D1", bg: "#E1F5FE" },
};

function getStatusBadge(r: { reply_status: string }): { label: string; color: string; bg: string } {
  if (r.reply_status === "replied") return { label: "Replied", color: "#4CAF50", bg: "#E8F5E9" };
  if (r.reply_status === "waiting") return { label: "Waiting Reply", color: "#E53935", bg: "#FFEBEE" };
  if (r.reply_status === "seen") return { label: "Seen (Not Replied)", color: "#F5A623", bg: "#FFF8E1" };
  return { label: "Waiting Reply", color: "#E53935", bg: "#FFEBEE" };
}

const statusTabs: StatusTab[] = [
  { key: "all", label: "All" },
  { key: "replied", label: "Replied" },
  { key: "waiting", label: "Waiting Reply" },
  { key: "closed", label: "Archived" },
];

function getProviderBadge(
  provider: InboxProvider | null | undefined,
): { label: string; color: string; bg: string } | null {
  if (!provider) return null;
  const display = provider.label ?? provider.oauth_email ?? provider.smtp_username;
  if (provider.provider === "gmail") {
    return { label: display ?? "Gmail", color: "#DB4437", bg: "#FCECEA" };
  }
  if (provider.provider === "microsoft365") {
    return { label: display ?? "Outlook", color: "#0078D4", bg: "#EDF3FB" };
  }
  return { label: display ?? "Custom", color: "#607D8B", bg: "#ECEFF1" };
}

function stripHtml(html: string | null | undefined): string {
  if (!html) return '';
  return html.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
}

function getInitials(name: string | null | undefined): string {
  if (!name) return "?";
  return name
    .split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);
}

function formatReceivedAt(iso: string): string {
  const d = new Date(iso);
  const now = new Date();
  const diff = Math.floor((now.getTime() - d.getTime()) / 1000);
  if (diff < 60) return `${diff}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
  return d.toLocaleDateString();
}

async function fetchSuggestion(id: string): Promise<string> {
  const res = await get<{ suggestion: string }>(API_URL.INBOX.SUGGEST_REPLY(id));
  return res?.data?.suggestion?.trim() ?? "";
}

export default function RepliesPage() {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [filterCategory, setFilterCategory] = useState<"workflow" | "sequence" | "campaign" | null>(null);
  const [filterValue, setFilterValue] = useState("");
  const [providerFilter, setProviderFilter] = useState("");
  const [activeStatus, setActiveStatus] = useState(() => {
    const tab = searchParams.get("tab");
    const validTabs = ["all", "replied", "seen", "waiting", "closed", "auto_paused"];
    return validTabs.includes(tab ?? "") ? (tab as string) : "all";
  });
  const [page, setPage] = useState(1);
  const [selectedReply, setSelectedReply] = useState<string | null>(null);
  const [detailCollapsed, setDetailCollapsed] = useState(false);
  const [showCompose, setShowCompose] = useState(false);
  const [replyEditorEmpty, setReplyEditorEmpty] = useState(true);
  const replyEditorRef = useRef<ReplyRichEditorHandle>(null);
  // Ref to track the open thread ID for useInboxRealtime without re-creating the Pusher subscription
  const selectedReplyRef = useRef<string | null>(selectedReply);
  const [sentBanner, setSentBanner] = useState(false);
  const [showArchiveConfirm, setShowArchiveConfirm] = useState(false);
  const composeRef = useRef<HTMLDivElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Track thread IDs that had a new inbound message arrive after page load.
  // Snapshot maps thread_id → last_message_at so we detect replies on existing
  // threads (not just brand-new threads entering the list).
  const [newThreadIds, setNewThreadIds] = useState<Set<string>>(new Set());
  const seenOnMountRef = useRef<Map<string, string> | null>(null);

  const [aiReply, setAiReply] = useState<string | null>(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Debounce search
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => setDebouncedSearch(search), 300);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [search]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, filterCategory, filterValue, providerFilter, activeStatus]);

  const queryClient = useQueryClient();

  const params: InboxListParams = {
    ...(debouncedSearch && { search: debouncedSearch }),
    ...(activeStatus !== "all" && { status: activeStatus }),
    ...(filterCategory === "campaign" && filterValue && { campaign_id: filterValue }),
    ...(filterCategory === "sequence" && filterValue && { sequence_id: filterValue }),
    ...(filterCategory === "workflow" && filterValue && { workflow_id: filterValue }),
    ...(providerFilter && { provider_id: providerFilter }),
    per_page: 20,
    page,
    sort: "last_message_at",
    order: "desc",
  };

  const { data: inboxData, isLoading } = useInboxQuery(params);
  const { data: filtersData } = useInboxFiltersQuery();
  const { data: detailData } = useInboxMessageQuery(selectedReply);
  const activeDetail = detailData?.data as InboxThreadDetail | undefined;

  // Global counts — always exclude the status tab filter so the header totals
  // stay consistent regardless of which tab is active.
  const baseCountsParams = {
    ...(debouncedSearch && { search: debouncedSearch }),
    ...(filterCategory === "campaign" && filterValue && { campaign_id: filterValue }),
    ...(filterCategory === "sequence" && filterValue && { sequence_id: filterValue }),
    ...(filterCategory === "workflow" && filterValue && { workflow_id: filterValue }),
    ...(providerFilter && { provider_id: providerFilter }),
  };
  const { data: globalCounts } = useInboxCountsQuery(baseCountsParams);

  const messages = inboxData?.data?.messages ?? [];
  const meta = inboxData?.meta as InboxMeta | undefined;
  const counts = globalCounts ??
    inboxData?.data?.counts ?? { total: 0, replied: 0, seen: 0, waiting: 0, closed: 0, auto_paused: 0 };
  const campaigns = filtersData?.data?.campaigns ?? [];
  const sequences = filtersData?.data?.sequences ?? [];
  const workflows = filtersData?.data?.workflows ?? [];
  const providers = filtersData?.data?.providers ?? [];

  const replyMutation = useReplyMutation();
  const updateStatusMutation = useUpdateStatusMutation();
  const toggleStarMutation = useToggleStarMutation();
  const markReadMutation = useMarkReadMutation();
  const syncMutation = useSyncInboxMutation();

  // Keep selectedReplyRef in sync so useInboxRealtime can read it via ref
  // (reading via ref avoids re-creating the Pusher channel subscription)
  useEffect(() => {
    selectedReplyRef.current = selectedReply;
  }, [selectedReply]);

  // On first data load, snapshot thread_id → last_message_at so we can detect
  // when a contact replies to a thread that already existed on page load.
  useEffect(() => {
    if (seenOnMountRef.current !== null || messages.length === 0) return;
    const snap = new Map<string, string>();
    messages.forEach((m) => snap.set(m.id, m.last_message_at));
    seenOnMountRef.current = snap;
  }, [messages]);

  // After each re-fetch (triggered by Pusher), detect threads where the contact
  // sent a message that is newer than what we saw on mount → mark as NEW.
  useEffect(() => {
    if (seenOnMountRef.current === null) return;
    const incoming = messages.filter((m) => {
      if (m.last_message_by !== "contact") return false;
      const seenAt = seenOnMountRef.current!.get(m.id);
      // New thread not in snapshot, or existing thread with a newer message
      return !seenAt || m.last_message_at > seenAt;
    });
    if (incoming.length === 0) return;
    setNewThreadIds((prev) => {
      const next = new Set(prev);
      incoming.forEach((m) => next.add(m.id));
      return next;
    });
    // Advance the snapshot timestamps so the same message isn't re-flagged
    incoming.forEach((m) => seenOnMountRef.current!.set(m.id, m.last_message_at));
  }, [messages]);

  // Subscribe to Pusher real-time events — refreshes the inbox
  // automatically when a reply is received or an email is opened.
  useInboxRealtime(selectedReplyRef);

  // Auto-select thread from ?thread= URL param (e.g. navigated from dashboard)
  useEffect(() => {
    const threadId = searchParams.get("thread");
    if (threadId) {
      setSelectedReply(threadId);
      setDetailCollapsed(false);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Scroll to bottom of thread messages when loaded via ?scroll=1 URL param
  useEffect(() => {
    if (!activeDetail || !searchParams.get("scroll")) return;
    setTimeout(() => {
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
    }, 150);
  }, [activeDetail?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const { data: providersData } = useEmailProvidersQuery();
  const navigate = useNavigate();
  const expiredProviders = (providersData?.data ?? []).filter((p: any) => p.token_expired);

  const active = selectedReply
    ? (messages.find((m) => m.id === selectedReply) ?? (activeDetail?.id === selectedReply ? activeDetail : null))
    : null;

  // Auto-mark-read when selecting an unread message
  useEffect(() => {
    if (selectedReply && active && active.last_message_by !== "admin") {
      markReadMutation.mutate(selectedReply);
    }
  }, [selectedReply]); // eslint-disable-line react-hooks/exhaustive-deps

  // Reset compose + detail state when switching messages
  useEffect(() => {
    setShowCompose(false);
    setReplyEditorEmpty(true);
    setSentBanner(false);
    setDetailCollapsed(false);
    setAiReply(null);
    setAiError(false);
    setAiLoading(!!selectedReply);
  }, [selectedReply]);

  // Generate AI suggested reply whenever the active thread or its messages load
  useEffect(() => {
    if (!active) {
      setAiLoading(false);
      return;
    }
    // Guard against stale detail data — wait until the detail for the selected thread is loaded
    if (!activeDetail || activeDetail.id !== selectedReply) return;

    setAiLoading(true);
    setAiError(false);
    fetchSuggestion(selectedReply!)
      .then((result) => setAiReply(result || null))
      .catch(() => {
        setAiReply(null);
        setAiError(true);
      })
      .finally(() => setAiLoading(false));
  }, [selectedReply, activeDetail?.id, activeDetail?.messages?.length]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (showCompose && composeRef.current) {
      composeRef.current.scrollIntoView({ behavior: "smooth", block: "end" });
      setTimeout(() => replyEditorRef.current?.focus(), 50);
    }
  }, [showCompose]);

  const handleReply = () => setShowCompose(true);
  const handleUseAiReply = (text: string) => {
    setShowCompose(true);
    const html = `<p>${text.replace(/\n\n/g, "</p><p>").replace(/\n/g, "<br/>")}</p>`;
    setTimeout(() => replyEditorRef.current?.setContent(html), 50);
  };

  const handleSend = () => {
    if (replyEditorEmpty || !selectedReply) return;
    const html = replyEditorRef.current?.getHTML() ?? "";
    if (!html.trim()) return;
    replyMutation.mutate(
      { id: selectedReply, body: html },
      {
        onSuccess: () => {
          setShowCompose(false);
          setReplyEditorEmpty(true);
          setSentBanner(true);
          setTimeout(() => setSentBanner(false), 3000);
        },
      },
    );
  };

  const positiveCount = counts.replied; // used in KPI bar

  return (
    <div className="rp-page">
      {expiredProviders.length > 0 && (
        <div className="rp-token-warning">
          <span>
            ⚠️ Email account <strong>{expiredProviders.map((p: any) => p.oauth_email).join(", ")}</strong> has
            disconnected. Replies may not be received until you reconnect.
          </span>
          <button onClick={() => navigate("/app/settings?tab=Email+Setup")}>Reconnect →</button>
        </div>
      )}
      <div className="rp-topbar">
        <div className="rp-topbar-left">
          <h1 className="rp-title">Replies</h1>
          <div className="rp-kpis">
            <span className="rp-kpi">
              {counts.total} <span className="rp-kpi-sub">total</span>
            </span>
            <span className="rp-kpi-dot" />
            <span className="rp-kpi rp-kpi--red">
              {counts.replied} <span className="rp-kpi-sub">need reply</span>
            </span>
          </div>
        </div>
        <button
          className="rp-icon-btn"
          onClick={() => syncMutation.mutate()}
          disabled={syncMutation.isPending}
          title="Sync inbox"
        >
          <RefreshCw size={15} className={syncMutation.isPending ? "rp-spin" : ""} />
        </button>
      </div>

      <div className="rp-toolbar">
        <div className="rp-search-bar">
          <Search size={16} className="rp-search-icon" />
          <input
            className="rp-search-input"
            placeholder="Search by name, company, or message..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          {search && (
            <button
              className="rp-search-clear"
              onClick={() => {
                setSearch("");
                setDebouncedSearch("");
              }}
            >
              <X size={14} />
            </button>
          )}
        </div>
        <div className="rp-toolbar-filters">
          <div className="rp-filter-pills">
            {(["workflow", "sequence", "campaign"] as const).map((cat) => (
              <button
                key={cat}
                className={`rp-pill ${filterCategory === cat ? "rp-pill--active" : ""}`}
                onClick={() => {
                  if (filterCategory === cat) {
                    setFilterCategory(null);
                    setFilterValue("");
                  } else {
                    setFilterCategory(cat);
                    setFilterValue("");
                  }
                }}
              >
                {cat.charAt(0).toUpperCase() + cat.slice(1)}
              </button>
            ))}
          </div>
          {filterCategory && (
            <div className="rp-select-wrap">
              <select className="rp-select" value={filterValue} onChange={(e) => setFilterValue(e.target.value)}>
                <option value="">All {filterCategory.charAt(0).toUpperCase() + filterCategory.slice(1)}s</option>
                {filterCategory === "campaign" &&
                  campaigns.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                {filterCategory === "sequence" &&
                  sequences.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name}
                    </option>
                  ))}
                {filterCategory === "workflow" &&
                  workflows.map((w) => (
                    <option key={w.id} value={w.id}>
                      {w.title}
                    </option>
                  ))}
              </select>
              <ChevronDown size={14} className="rp-select-arrow" />
            </div>
          )}
          {providers.length > 1 && (
            <div className="rp-select-wrap">
              <select className="rp-select" value={providerFilter} onChange={(e) => setProviderFilter(e.target.value)}>
                <option value="">All Inboxes</option>
                {providers.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.label ?? p.oauth_email ?? p.smtp_username}
                  </option>
                ))}
              </select>
              <ChevronDown size={14} className="rp-select-arrow" />
            </div>
          )}
          {(filterCategory || filterValue || providerFilter || search) && (
            <button
              className="rp-reset-btn"
              onClick={() => {
                setFilterCategory(null);
                setFilterValue("");
                setProviderFilter("");
                setSearch("");
                setDebouncedSearch("");
                setActiveStatus("all");
              }}
            >
              <X size={12} /> Clear
            </button>
          )}
        </div>
      </div>

      <div className="rp-status-tabs">
        {statusTabs.map((t) => (
          <button
            key={t.key}
            className={`rp-stab ${activeStatus === t.key ? "rp-stab--active" : ""}`}
            onClick={() => {
              setActiveStatus(t.key);
              queryClient.invalidateQueries({ queryKey: ["inbox"] });
              queryClient.invalidateQueries({ queryKey: ["inbox-counts"] });
            }}
          >
            {t.label}
            {t.key === "replied" && counts.replied > 0 && <span className="rp-stab-badge">{counts.replied}</span>}
            {t.key === "seen" && counts.seen > 0 && (
              <span className="rp-stab-badge rp-stab-badge--amber">{counts.seen}</span>
            )}
            {t.key === "waiting" && counts.waiting > 0 && (
              <span className="rp-stab-badge rp-stab-badge--gray">{counts.waiting}</span>
            )}
            {t.key === "closed" && counts.closed > 0 && (
              <span className="rp-stab-badge rp-stab-badge--gray">{counts.closed}</span>
            )}
          </button>
        ))}
        <span className="rp-result-count">{meta ? meta.total : counts.total} results</span>
      </div>

      <div className="rp-content">
        <div className={`rp-list ${active && !detailCollapsed ? "rp-list--narrow" : ""}`}>
          {isLoading ? (
            Array.from({ length: 4 }).map((_, i) => <div key={i} className="rp-card rp-card--skeleton" />)
          ) : messages.length === 0 ? (
            <div className="rp-empty">
              <MessageSquare size={28} color="var(--gray-300)" />
              <p>No replies match your filters</p>
            </div>
          ) : (
            messages.map((r) => {
              const sent = sentimentConfig[r.sentiment ?? "neutral"] ?? sentimentConfig["neutral"];
              const stat = getStatusBadge(r);
              const leadName = r.contact_name ?? r.contact_email;
              const company = r.contact_company ?? "";
              const titleStr = r.contact_title ?? "";
              const badge = getProviderBadge(r.sender_profile?.email_provider_config ?? null);
              const isUnread = !r.is_seen && r.last_message_by === "contact";
              const isNew = newThreadIds.has(r.id);
              const borderColor = stat.color;
              return (
                <div
                  key={r.id}
                  className={`rp-card ${selectedReply === r.id ? "rp-card--active" : ""} ${isUnread ? "rp-card--unread" : ""}`}
                  style={{ borderLeftColor: borderColor, borderLeftWidth: 3, borderLeftStyle: "solid" }}
                  onClick={() => {
                    if (selectedReply === r.id && detailCollapsed) {
                      setDetailCollapsed(false);
                      queryClient.refetchQueries({ queryKey: ['inbox', r.id], exact: true });
                    } else {
                      setSelectedReply(r.id);
                    }
                    setNewThreadIds((prev) => { const s = new Set(prev); s.delete(r.id); return s; });
                  }}
                >
                  <div className="rp-card-row1">
                    <div className="rp-card-avatar">
                      {r.contact_photo_url ? (
                        <img src={r.contact_photo_url} alt={leadName ?? ""} className="rp-avatar-img" />
                      ) : (
                        getInitials(leadName)
                      )}
                    </div>
                    <div className="rp-card-info">
                      <div className="rp-card-nameline">
                        <span className="rp-card-name">{leadName}</span>
                        <div className="rp-card-nameline-right">
                          {isNew && <span className="rp-new-dot" title="New reply" />}
                          <span className="rp-card-time">{formatReceivedAt(r.last_message_at)}</span>
                        </div>
                      </div>
                      <span className="rp-card-company">
                        {titleStr}
                        {titleStr && company ? ", " : ""}
                        {company}
                      </span>
                    </div>
                  </div>
                  <p className="rp-card-subject">{r.subject ?? "(no subject)"}</p>
                  <p className="rp-card-snippet">
                    <span className="rp-card-snippet-who">
                      {r.last_message_by === "contact" ? (r.contact_name?.split(" ")[0] ?? "Them") : "You"}:
                    </span>{" "}
                    {stripHtml(r.last_message_preview)}
                  </p>
                  <div className="rp-card-tags">
                    {r.reply_status === "replied" &&
                      sent &&
                      (() => {
                        const SentIcon = sent.icon;
                        return (
                          <span className="rp-tag" style={{ background: sent.bg, color: sent.color }}>
                            <SentIcon size={10} /> {sent.label}
                          </span>
                        );
                      })()}
                    <span className="rp-tag" style={{ background: stat.bg, color: stat.color }}>
                      {stat.label}
                    </span>
                    {badge && (
                      <span className="rp-tag" style={{ background: badge.bg, color: badge.color }}>
                        {badge.label}
                      </span>
                    )}
                    {r.is_starred && <Star size={12} color="var(--amber)" fill="var(--amber)" />}
                  </div>
                </div>
              );
            })
          )}

          {meta &&
            meta.total_pages > 1 &&
            (() => {
              const total = meta.total_pages;
              const cur = meta.page;
              // Build page slots: always show 1 and last, window of 3 around current
              const slots: (number | "...")[] = [];
              const addSlot = (n: number | "...") => {
                if (slots[slots.length - 1] !== n) slots.push(n);
              };
              addSlot(1);
              if (cur - 2 > 2) addSlot("...");
              for (let i = Math.max(2, cur - 1); i <= Math.min(total - 1, cur + 1); i++) addSlot(i);
              if (cur + 2 < total - 1) addSlot("...");
              if (total > 1) addSlot(total);

              return (
                <div className="rp-pagination">
                  <button
                    className="rp-page-btn"
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={cur <= 1}
                    title="Previous page"
                  >
                    <ChevronLeft size={15} />
                  </button>
                  {slots.map((s, i) =>
                    s === "..." ? (
                      <span key={`ellipsis-${i}`} className="rp-page-ellipsis">
                        …
                      </span>
                    ) : (
                      <button
                        key={s}
                        className={`rp-page-btn ${s === cur ? "rp-page-btn--active" : ""}`}
                        onClick={() => setPage(s as number)}
                      >
                        {s}
                      </button>
                    ),
                  )}
                  <button
                    className="rp-page-btn"
                    onClick={() => setPage((p) => p + 1)}
                    disabled={!meta.has_more}
                    title="Next page"
                  >
                    <ChevronRight size={15} />
                  </button>
                </div>
              );
            })()}
        </div>

        {active &&
          !detailCollapsed &&
          (() => {
            const sent = sentimentConfig[active.sentiment ?? "neutral"] ?? sentimentConfig["neutral"];
            const leadName = active.contact_name ?? active.contact_email;
            const titleStr = active.contact_title ?? "";
            const company = active.contact_company ?? "";
            const badge = getProviderBadge(active.sender_profile?.email_provider_config ?? null);
            // aiReply is generated via Azure AI in useEffect above

            const replyDisabled = activeDetail?.sequence_on_reply === false;

            return (
              <div className="rp-detail">
                <div className="rp-detail-head">
                  <button className="rp-back-btn" onClick={() => setSelectedReply(null)}>
                    <ArrowLeft size={16} />
                  </button>
                  <div className="rp-detail-avatar">
                    {active.contact_photo_url ? (
                      <img src={active.contact_photo_url} alt={leadName ?? ""} className="rp-avatar-img" />
                    ) : (
                      getInitials(leadName)
                    )}
                  </div>
                  <div className="rp-detail-who">
                    <span className="rp-detail-name">{leadName}</span>
                    <span className="rp-detail-role">
                      {titleStr}
                      {titleStr && company ? " at " : ""}
                      {company}
                    </span>
                  </div>
                  <div className="rp-detail-actions">
                    {!replyDisabled && (
                      <button className="btn btn-primary btn-sm" onClick={handleReply}>
                        <Reply size={14} /> Reply
                      </button>
                    )}
                    {active.reply_status === "closed" ? (
                      <button
                        className="btn btn-secondary btn-sm"
                        disabled={updateStatusMutation.isPending}
                        title="Restore to inbox"
                        onClick={() => {
                          updateStatusMutation.mutate(
                            { id: active.id, status: "waiting" },
                            { onSuccess: () => setSelectedReply(null) },
                          );
                        }}
                      >
                        <RotateCcw size={14} />
                        {updateStatusMutation.isPending ? "Restoring…" : "Restore"}
                      </button>
                    ) : (
                      <button className="rp-icon-btn" title="Archive" onClick={() => setShowArchiveConfirm(true)}>
                        <Archive size={15} />
                      </button>
                    )}
                    <button
                      className="rp-icon-btn"
                      title={active.is_starred ? "Unstar" : "Star"}
                      onClick={() => toggleStarMutation.mutate({ id: active.id, is_starred: !active.is_starred })}
                    >
                      <Star
                        size={15}
                        fill={active.is_starred ? "var(--amber)" : "none"}
                        color={active.is_starred ? "var(--amber)" : "currentColor"}
                      />
                    </button>
                    <button
                      className="rp-icon-btn"
                      title="Collapse detail panel"
                      onClick={() => setDetailCollapsed(true)}
                    >
                      <PanelRightClose size={15} />
                    </button>
                  </div>
                </div>

                <div className="rp-detail-meta">
                  {active.campaign && (
                    <span className="rp-meta-item">
                      <span className="rp-meta-label">Campaign</span> {active.campaign.name}
                    </span>
                  )}
                  {badge && (
                    <span className="rp-meta-item">
                      <span className="rp-meta-label">From Inbox</span> {badge.label}
                    </span>
                  )}
                  {active.reply_status === "replied" && sent && (
                    <span className="rp-tag" style={{ background: sent.bg, color: sent.color }}>
                      {sent.label}
                    </span>
                  )}
                </div>

                <div className="rp-thread">
                  {(activeDetail?.messages ?? []).length > 0
                    ? activeDetail!.messages.map((msg) => {
                        const isFullDoc = /^\s*<!doctype|^\s*<html/i.test(msg.body_html ?? "");
                        return (
                          <div
                            key={msg.id}
                            className={`rp-msg ${msg.direction === "outbound" ? "rp-msg--out" : "rp-msg--in"}`}
                          >
                            <div className="rp-msg-head">
                              {msg.direction === "outbound" && (
                                <div className="rp-msg-avatar rp-msg-avatar--out">
                                  {user?.avatarUrl ? (
                                    <img src={user.avatarUrl} alt="" className="rp-avatar-img" />
                                  ) : (
                                    [user?.firstName?.[0], user?.lastName?.[0]]
                                      .filter(Boolean)
                                      .join("")
                                      .toUpperCase() || "?"
                                  )}
                                </div>
                              )}
                              {msg.direction === "inbound" && (
                                <div className="rp-msg-avatar rp-msg-avatar--in">
                                  {active.contact_photo_url ? (
                                    <img src={active.contact_photo_url} alt="" className="rp-avatar-img" />
                                  ) : (
                                    getInitials(leadName)
                                  )}
                                </div>
                              )}
                              <span className="rp-msg-who">
                                {msg.direction === "outbound" ? (msg.from_name ?? msg.from_email) : leadName}
                              </span>
                              <span className="rp-msg-when">{formatReceivedAt(msg.sent_at)}</span>
                            </div>
                            {msg.body_html ? (
                              isFullDoc ? (
                                <iframe
                                  className="rp-msg-iframe"
                                  srcDoc={msg.body_html}
                                  sandbox="allow-same-origin"
                                  onLoad={(e) => {
                                    const f = e.currentTarget;
                                    const h =
                                      f.contentDocument?.documentElement?.scrollHeight ??
                                      f.contentDocument?.body?.scrollHeight;
                                    if (h) f.style.height = h + "px";
                                  }}
                                />
                              ) : (
                                <div className="rp-msg-body" dangerouslySetInnerHTML={{ __html: msg.body_html }} />
                              )
                            ) : (
                              <p className="rp-msg-body">{msg.body_text ?? ""}</p>
                            )}
                          </div>
                        );
                      })
                    : activeDetail?.original_email && (
                        <div className="rp-msg rp-msg--out">
                          <div className="rp-msg-head">
                            <span className="rp-msg-who">
                              {activeDetail.original_email.from_name ?? activeDetail.original_email.from_email}
                            </span>
                            <span className="rp-msg-when">Original outreach</span>
                          </div>
                          <p
                            className="rp-msg-body"
                            dangerouslySetInnerHTML={{ __html: activeDetail.original_email.email_body ?? "" }}
                          />
                        </div>
                      )}
                  <div ref={messagesEndRef} />
                </div>

                {replyDisabled && (
                  <div className="rp-reply-disabled-notice">
                    Reply handling is disabled for this sequence. Enable it in Sequence → Options.
                  </div>
                )}

                {active && !replyDisabled && (
                  <div className="rp-ai-box">
                    <Sparkles size={14} color="var(--amber)" />
                    <div className="rp-ai-content">
                      <p className="rp-ai-label">AI Suggested Reply</p>
                      {aiLoading ? (
                        <p className="rp-ai-text" style={{ color: "#9ca3af", fontStyle: "italic" }}>
                          Generating suggestion…
                        </p>
                      ) : aiReply ? (
                        <>
                          <p className="rp-ai-text">{aiReply}</p>
                          <button
                            className="btn btn-secondary btn-sm"
                            style={{ marginTop: 6, fontSize: 12 }}
                            onClick={() => handleUseAiReply(aiReply!)}
                          >
                            Use this reply
                          </button>
                        </>
                      ) : (
                        <p className="rp-ai-text" style={{ color: "#9ca3af", fontStyle: "italic" }}>
                          Unable to generate suggestion.
                        </p>
                      )}
                    </div>
                  </div>
                )}

                {sentBanner && (
                  <div className="rp-sent-banner">
                    <Send size={13} /> Reply sent successfully
                  </div>
                )}

                {showCompose && !replyDisabled && (
                  <div className="rp-compose" ref={composeRef}>
                    <div className="rp-compose-top">
                      <span className="rp-compose-label">Reply to {leadName}</span>
                      <button
                        className="rp-compose-cancel"
                        onClick={() => {
                          setShowCompose(false);
                          setReplyEditorEmpty(true);
                        }}
                      >
                        Cancel
                      </button>
                    </div>
                    <ReplyRichEditor
                      ref={replyEditorRef}
                      placeholder={`Write your reply to ${leadName.split(" ")[0]}...`}
                      onChange={(_html, isEmpty) => setReplyEditorEmpty(isEmpty)}
                      leadContext={{
                        name: leadName,
                        lastMessage: activeDetail?.messages?.slice(-1)[0]?.body_text ?? "",
                      }}
                    />
                    <div className="rp-compose-btns">
                      <button
                        className="btn btn-primary btn-sm"
                        style={{ fontSize: 12, opacity: replyEditorEmpty ? 0.5 : 1 }}
                        onClick={handleSend}
                        disabled={replyMutation.isPending || replyEditorEmpty}
                      >
                        <Send size={13} /> {replyMutation.isPending ? "Sending…" : "Send"}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })()}
      </div>

      {/* ── Archive confirmation modal ── */}
      {showArchiveConfirm && active && (
        <div className="rp-confirm-overlay" onClick={() => setShowArchiveConfirm(false)}>
          <div className="rp-confirm-modal" onClick={(e) => e.stopPropagation()}>
            <div className="rp-confirm-icon">
              <Archive size={28} color="#E53935" />
            </div>
            <h3 className="rp-confirm-title">Archive this thread?</h3>
            <p className="rp-confirm-desc">
              This conversation will be moved to the archive and removed from your active inbox. You can still find it
              later by filtering by Archived status.
            </p>
            <div className="rp-confirm-actions">
              <button className="btn btn-secondary" onClick={() => setShowArchiveConfirm(false)}>
                Cancel
              </button>
              <button
                className="btn btn-danger"
                disabled={updateStatusMutation.isPending}
                onClick={() => {
                  updateStatusMutation.mutate(
                    { id: active.id, status: "closed" },
                    {
                      onSuccess: () => {
                        setShowArchiveConfirm(false);
                        setSelectedReply(null);
                      },
                    },
                  );
                }}
              >
                <Archive size={14} />
                {updateStatusMutation.isPending ? "Archiving…" : "Archive"}
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        .rp-page { max-width: 1400px; margin: 0 auto; padding: 20px 24px; }

        .rp-topbar { display: flex; align-items: center; justify-content: space-between; margin-bottom: 14px; }
        .rp-topbar-left { display: flex; align-items: baseline; gap: 16px; flex-wrap: wrap; }
        .rp-title { font-size: 24px; font-weight: 700; color: var(--navy); margin: 0; }
        .rp-kpis { display: flex; align-items: center; gap: 8px; }
        .rp-kpi { font-size: 13px; font-weight: 600; color: var(--navy); }
        .rp-kpi-sub { font-weight: 400; color: var(--gray-400); }
        .rp-kpi--red { color: #E53935; }
        .rp-kpi--green { color: #4CAF50; }
        .rp-kpi-dot { width: 3px; height: 3px; border-radius: 50%; background: var(--gray-300); }

        .rp-toolbar { display: flex; gap: 10px; margin-bottom: 10px; align-items: center; flex-wrap: wrap; }
        .rp-search-bar {
          display: flex; align-items: center; flex: 1; min-width: 220px;
          background: white; border: 1px solid var(--gray-200); border-radius: 8px;
          padding: 0 12px; transition: border-color 0.2s, box-shadow 0.2s;
        }
        .rp-search-bar:focus-within { border-color: var(--sky); box-shadow: 0 0 0 3px rgba(74,173,232,0.08); }
        .rp-search-icon { color: var(--gray-400); flex-shrink: 0; }
        .rp-search-input {
          flex: 1; border: none; outline: none; padding: 8px 10px; font-size: 13px;
          color: var(--gray-700); background: transparent; font-family: inherit;
        }
        .rp-search-input::placeholder { color: var(--gray-400); }
        .rp-search-clear {
          background: none; border: none; padding: 2px; color: var(--gray-400);
          cursor: pointer; border-radius: 4px; display: flex; align-items: center;
        }
        .rp-search-clear:hover { color: var(--gray-600); background: var(--gray-100); }

        .rp-toolbar-filters { display: flex; gap: 8px; }
        .rp-select-wrap { position: relative; }
        .rp-select {
          appearance: none; border: 1px solid var(--gray-200); border-radius: 8px;
          padding: 8px 30px 8px 12px; font-size: 13px; color: var(--gray-600);
          background: white; cursor: pointer; font-family: inherit; outline: none;
        }
        .rp-select:focus { border-color: var(--sky); }
        .rp-select-arrow { position: absolute; right: 10px; top: 50%; transform: translateY(-50%); color: var(--gray-400); pointer-events: none; }

        .rp-status-tabs {
          display: flex; align-items: center; gap: 2px; border-bottom: 1px solid var(--gray-200);
          margin-bottom: 14px; overflow-x: auto; scrollbar-width: none;
        }
        .rp-status-tabs::-webkit-scrollbar { display: none; }
        .rp-stab {
          padding: 8px 14px; font-size: 13px; font-weight: 500; color: var(--gray-500);
          background: none; border: none; border-bottom: 2px solid transparent;
          margin-bottom: -1px; cursor: pointer; white-space: nowrap;
          display: inline-flex; align-items: center; gap: 6px; transition: color 0.15s;
        }
        .rp-stab:hover { color: var(--navy); }
        .rp-stab--active { color: var(--sky); border-bottom-color: var(--sky); }
        .rp-stab-badge {
          background: #FFEBEE; color: #E53935; font-size: 11px; font-weight: 700;
          padding: 1px 7px; border-radius: 10px;
        }
        .rp-result-count { margin-left: auto; font-size: 12px; color: var(--gray-400); white-space: nowrap; padding: 8px 0; }

        .rp-content { display: flex; gap: 14px; min-height: 480px; }
        .rp-list { flex: 1; display: flex; flex-direction: column; gap: 6px; overflow-y: auto; max-height: calc(100vh - 240px); }
        .rp-list--narrow { max-width: 380px; flex-shrink: 0; }
        .rp-empty { text-align: center; padding: 48px 20px; color: var(--gray-400); }
        .rp-empty p { margin-top: 8px; font-size: 13px; }

        .rp-card {
          background: white; border: 1px solid var(--gray-200); border-radius: 8px;
          padding: 12px 14px; cursor: pointer; transition: all 0.12s;
        }
        .rp-card:hover { border-color: var(--sky); }
        .rp-card--active { border-color: var(--sky); background: #f0f7fd; }
        .rp-card--unread { border-left: 3px solid var(--sky); }
        .rp-card--unread .rp-card-name { font-weight: 700; font-size: 15px; }
        .rp-card--unread .rp-card-subject { font-weight: 700; font-size: 15px; color: var(--navy); }
        .rp-card--unread .rp-card-snippet { color: var(--gray-700); font-weight: 700; font-size: 15px; }
        .rp-card-nameline-right { display: flex; align-items: center; gap: 5px; }
        .rp-new-dot { display: inline-block; width: 8px; height: 8px; border-radius: 50%; background: var(--green); flex-shrink: 0; animation: rp-pulse 1.5s ease-in-out infinite; }
        @keyframes rp-pulse { 0%,100% { opacity: 1; } 50% { opacity: 0.4; } }
        .rp-card-snippet-who { font-weight: 600; color: var(--navy); }
        .rp-card--skeleton {
          height: 100px; background: var(--gray-100);
          animation: rp-shimmer 1.4s ease-in-out infinite;
        }
        @keyframes rp-shimmer {
          0%, 100% { opacity: 1; } 50% { opacity: 0.5; }
        }
        .rp-card-row1 { display: flex; gap: 10px; align-items: center; margin-bottom: 6px; }
        .rp-card-avatar {
          width: 32px; height: 32px; border-radius: 50%; background: var(--sky-light);
          color: var(--sky); display: flex; align-items: center; justify-content: center;
          font-size: 11px; font-weight: 600; flex-shrink: 0; overflow: hidden;
        }
        .rp-card-info { flex: 1; min-width: 0; }
        .rp-card-nameline { display: flex; justify-content: space-between; align-items: center; }
        .rp-card-name { font-size: 13px; font-weight: 600; color: var(--navy); }
        .rp-card-time { font-size: 11px; color: var(--gray-400); }
        .rp-card-company { font-size: 11px; color: var(--gray-500); display: block; margin-top: 1px; }
        .rp-card-subject { font-size: 12px; font-weight: 600; color: var(--navy); margin: 0 0 2px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
        .rp-card-snippet { font-size: 12px; color: var(--gray-500); margin: 0 0 8px; line-height: 1.4; display: -webkit-box; -webkit-line-clamp: 1; -webkit-box-orient: vertical; overflow: hidden; }
        .rp-card-tags { display: flex; align-items: center; gap: 5px; flex-wrap: wrap; }
        .rp-tag {
          display: inline-flex; align-items: center; gap: 3px;
          padding: 2px 7px; border-radius: 12px; font-size: 10px; font-weight: 600;
        }

        .rp-detail {
          flex: 1; background: white; border: 1px solid var(--gray-200);
          border-radius: 10px; padding: 16px; display: flex; flex-direction: column; gap: 12px;
          overflow-y: auto; max-height: calc(100vh - 240px);
        }
        .rp-detail-head { display: flex; align-items: center; gap: 10px; }
        .rp-back-btn {
          display: none; background: none; border: none; padding: 4px; cursor: pointer;
          color: var(--gray-500); border-radius: 6px;
        }
        .rp-back-btn:hover { background: var(--gray-100); color: var(--navy); }
        .rp-detail-avatar {
          width: 36px; height: 36px; border-radius: 50%; background: var(--sky-light);
          color: var(--sky); display: flex; align-items: center; justify-content: center;
          font-size: 12px; font-weight: 600; flex-shrink: 0; overflow: hidden;
        }
        .rp-avatar-img { width: 100%; height: 100%; object-fit: cover; border-radius: 50%; }
        .rp-detail-who { flex: 1; min-width: 0; }
        .rp-detail-name { font-size: 15px; font-weight: 600; color: var(--navy); display: block; }
        .rp-detail-role { font-size: 12px; color: var(--gray-500); }
        .rp-detail-actions { display: flex; gap: 6px; align-items: center; }
        .rp-icon-btn {
          background: none; border: 1px solid var(--gray-200); border-radius: 6px;
          padding: 5px 6px; cursor: pointer; color: var(--gray-400); display: flex;
          align-items: center; transition: all 0.15s;
        }
        .rp-icon-btn:hover { border-color: var(--sky); color: var(--sky); }
        .rp-icon-btn:disabled { opacity: 0.5; cursor: not-allowed; }

        .rp-detail-meta {
          display: flex; align-items: center; gap: 12px; padding: 10px 14px;
          background: var(--gray-50); border-radius: 8px; flex-wrap: wrap;
        }
        .rp-meta-item { font-size: 12px; color: var(--gray-600); }
        .rp-meta-label { font-weight: 600; color: var(--gray-400); margin-right: 4px; text-transform: uppercase; font-size: 10px; letter-spacing: 0.3px; }

        .rp-thread { display: flex; flex-direction: column; gap: 8px; flex: 1; }
        .rp-msg { padding: 12px; border-radius: 8px; }
        .rp-msg--out { background: #f4f6f9; margin-left: 20px; }
        .rp-msg--in { background: #EDF6FD; margin-right: 20px; }
        .rp-msg-head { display: flex; align-items: center; gap: 8px; margin-bottom: 4px; }
        .rp-msg-avatar {
          width: 24px; height: 24px; border-radius: 50%; display: flex; align-items: center;
          justify-content: center; font-size: 9px; font-weight: 700; flex-shrink: 0; overflow: hidden;
        }
        .rp-msg-avatar--out { background: var(--sky); color: #fff; }
        .rp-msg-avatar--in { background: var(--sky-light); color: var(--sky); }
        .rp-msg-who { font-size: 11px; font-weight: 600; color: var(--navy); }
        .rp-msg-when { font-size: 10px; color: var(--gray-400); }
        .rp-msg-body { font-size: 13px; color: var(--gray-700); line-height: 1.5; margin: 0; }
        .rp-msg-iframe { width: 100%; border: none; display: block; min-height: 80px; }

        .rp-ai-box {
          display: flex; gap: 8px; padding: 12px; background: linear-gradient(135deg, #FFFBEB, #FFF7E0);
          border: 1px solid #FFE082; border-radius: 8px;
        }
        .rp-ai-content { flex: 1; }
        .rp-ai-label { font-size: 11px; font-weight: 700; color: var(--navy); margin: 0 0 3px; }
        .rp-ai-text { font-size: 12px; color: var(--gray-600); margin: 0; line-height: 1.5; }

        .rp-token-warning {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 12px;
          padding: 10px 20px;
          background: #fff7ed;
          border-bottom: 1px solid #fed7aa;
          font-size: 13px;
          color: #9a3412;
        }
        .rp-token-warning button {
          background: #ea580c;
          color: #fff;
          border: none;
          border-radius: 6px;
          padding: 5px 12px;
          font-size: 12px;
          font-weight: 600;
          cursor: pointer;
          white-space: nowrap;
        }
        .rp-reply-disabled-notice {
          padding: 10px 14px; background: #FFF8E1; color: #795548;
          border: 1px solid #FFE082; border-radius: 6px; font-size: 12px;
        }

        .rp-sent-banner {
          display: flex; align-items: center; gap: 6px; padding: 10px 14px;
          background: #E8F5E9; color: #2E7D32; border-radius: 6px;
          font-size: 12px; font-weight: 600; animation: rp-fade 0.3s ease;
        }
        @keyframes rp-fade { from { opacity: 0; transform: translateY(-3px); } to { opacity: 1; transform: translateY(0); } }

        .rp-compose { border: 1px solid var(--sky); border-radius: 8px; padding: 12px; background: #f9fbfd; }
        .rp-compose-top { display: flex; justify-content: space-between; align-items: center; margin-bottom: 8px; }
        .rp-compose-label { font-size: 12px; font-weight: 600; color: var(--navy); }
        .rp-compose-cancel { background: none; border: none; font-size: 12px; color: var(--gray-500); cursor: pointer; font-family: inherit; }
        .rp-compose-cancel:hover { color: var(--navy); }
        .rp-compose-input {
          width: 100%; border: 1px solid var(--gray-200); border-radius: 6px;
          padding: 10px; font-size: 13px; line-height: 1.5; resize: vertical;
          font-family: inherit; color: var(--gray-700); background: white;
          min-height: 80px; outline: none; box-sizing: border-box;
        }
        .rp-compose-input:focus { border-color: var(--sky); box-shadow: 0 0 0 2px rgba(74,173,232,0.1); }
        .rp-compose-btns { display: flex; justify-content: space-between; align-items: center; margin-top: 8px; }

        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        .rp-spin { animation: spin 0.8s linear infinite; }

        .rp-reset-btn {
          padding: 6px 12px; font-size: 12px; font-weight: 600; border-radius: 20px;
          border: 1px solid var(--gray-200); background: white; color: #E53935;
          cursor: pointer; display: inline-flex; align-items: center; gap: 4px;
          font-family: inherit; transition: all 0.15s;
        }
        .rp-reset-btn:hover { border-color: #E53935; background: #FFEBEE; }

        .rp-filter-pills { display: flex; gap: 6px; }
        .rp-pill {
          padding: 6px 12px; font-size: 12px; font-weight: 600; border-radius: 20px;
          border: 1px solid var(--gray-200); background: white; color: var(--gray-500);
          cursor: pointer; transition: all 0.15s; font-family: inherit;
        }
        .rp-pill:hover { border-color: var(--sky); color: var(--sky); }
        .rp-pill--active { border-color: var(--sky); background: #EDF6FD; color: var(--sky); }

        .rp-stab-badge--gray {
          background: #ECEFF1; color: #607D8B; font-size: 11px; font-weight: 700;
          padding: 1px 7px; border-radius: 10px;
        }
        .rp-stab-badge--amber {
          background: #FFF8E1; color: #F5A623; font-size: 11px; font-weight: 700;
          padding: 1px 7px; border-radius: 10px;
        }

        .rp-pagination {
          display: flex; align-items: center; justify-content: center; gap: 4px;
          padding: 16px 0 6px; border-top: 1px solid var(--gray-100); margin-top: 6px;
        }
        .rp-page-btn {
          min-width: 32px; height: 32px; padding: 0 10px;
          display: inline-flex; align-items: center; justify-content: center;
          font-size: 13px; font-weight: 500; border-radius: 7px;
          border: 1px solid var(--gray-200); background: white; color: var(--gray-600);
          cursor: pointer; transition: all 0.15s; font-family: inherit;
        }
        .rp-page-btn:hover:not(:disabled) { border-color: var(--sky); color: var(--sky); background: #EDF6FD; }
        .rp-page-btn--active { background: var(--sky); color: white; border-color: var(--sky); font-weight: 700; pointer-events: none; }
        .rp-page-btn:disabled { opacity: 0.35; cursor: not-allowed; }
        .rp-page-ellipsis { font-size: 13px; color: var(--gray-400); padding: 0 2px; line-height: 32px; }

        /* ── Archive confirmation modal ── */
        .rp-confirm-overlay {
          position: fixed; inset: 0; background: rgba(0,0,0,0.45);
          display: flex; align-items: center; justify-content: center;
          z-index: 1000; padding: 16px;
        }
        .rp-confirm-modal {
          background: white; border-radius: 12px; padding: 28px 28px 24px;
          max-width: 420px; width: 100%; box-shadow: 0 20px 60px rgba(0,0,0,0.18);
          display: flex; flex-direction: column; align-items: center; gap: 12px;
          animation: rp-modal-in 0.18s ease;
        }
        @keyframes rp-modal-in {
          from { opacity: 0; transform: scale(0.95) translateY(8px); }
          to   { opacity: 1; transform: scale(1) translateY(0); }
        }
        .rp-confirm-icon {
          width: 52px; height: 52px; border-radius: 50%;
          background: #FFEBEE; display: flex; align-items: center; justify-content: center;
        }
        .rp-confirm-title {
          margin: 4px 0 0; font-size: 17px; font-weight: 700; color: var(--navy);
          text-align: center;
        }
        .rp-confirm-desc {
          margin: 0; font-size: 13px; color: var(--gray-500); text-align: center;
          line-height: 1.55;
        }
        .rp-confirm-actions {
          display: flex; gap: 10px; margin-top: 6px; width: 100%; justify-content: flex-end;
        }
        .btn-danger {
          background: #E53935; color: white; border: none; border-radius: 8px;
          padding: 8px 16px; font-size: 13px; font-weight: 600; cursor: pointer;
          display: inline-flex; align-items: center; gap: 6px; transition: background 0.15s;
          font-family: inherit;
        }
        .btn-danger:hover:not(:disabled) { background: #c62828; }
        .btn-danger:disabled { opacity: 0.6; cursor: not-allowed; }

        @media (max-width: 768px) {
          .rp-page { padding: 12px; }
          .rp-content { flex-direction: column; }
          .rp-list { max-height: none; }
          .rp-list--narrow { max-width: 100%; }
          .rp-detail { max-height: none; }
          .rp-back-btn { display: flex; }
          .rp-toolbar { flex-direction: column; }
          .rp-toolbar-filters { width: 100%; flex-wrap: wrap; }
          .rp-select { width: 100%; }
          .rp-select-wrap { flex: 1; }
          .rp-kpis { display: none; }
          .rp-detail-meta { flex-direction: column; align-items: flex-start; gap: 6px; }
          .rp-detail-actions { flex-wrap: wrap; }
          .rp-result-count { display: none; }
        }
        @media (max-width: 480px) {
          .rp-title { font-size: 20px; }
          .rp-stab { padding: 6px 10px; font-size: 12px; }
          .rp-detail-head { flex-wrap: wrap; gap: 8px; }
          .rp-detail-actions { width: 100%; justify-content: flex-start; }
        }
      `}</style>
    </div>
  );
}
